import React, { useState } from 'react';
import { MoreVertical, Pencil, Trash2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { format } from 'date-fns';
import { 
  getCardBaseClasses, 
  getCardHeaderClasses, 
  getCardTitleClasses, 
  getCardDateClasses, 
  getCardMetadataClasses,
  getCardActionButtonClasses 
} from '@/components/ui/shared-card-styles';
import { cn } from '@/lib/utils';
import { TaskActiveDot } from '@/components/tasks/TaskActiveDot';

// --- Utility: Compute gradient edge color across multiple segments at fixed 'rainbow' stops.
// Red (0) → Orange (2.5) → Yellow (5) → Green (7.5) → Blue (10). Never compresses: blue only at index 10+
function getListEdgeColor(index: number, total: number) {
  const stops = [
    { pos: 0,   hue: 0 },    // Red
    { pos: 2.5, hue: 30 },   // Orange
    { pos: 5,   hue: 55 },   // Yellow
    { pos: 7.5, hue: 120 },  // Green
    { pos: 10,  hue: 210 },  // Blue
  ];

  // If index >= 10, snap to blue
  if (index >= 10) return "hsl(210,96%,54%)"; // blue at index 10+

  // If index <=0, snap to red
  if (index <= 0) return "hsl(0,90%,60%)"; // red

  // Find gradient stops this index falls between
  let left = stops[0], right = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (index >= stops[i].pos && index <= stops[i+1].pos) {
      left = stops[i];
      right = stops[i+1];
      break;
    }
  }
  const segT = (index - left.pos) / (right.pos - left.pos);

  // Gentle sat/light gradation across 0–10; not dependent on total
  const hue = left.hue + (right.hue - left.hue) * segT;
  const sat = 90 + (96 - 90) * (index / 10);    // sat: 90% (red) → 96% (blue)
  const light = 60 + (54 - 60) * (index / 10);  // light: 60% → 54%
  return `hsl(${hue},${sat}%,${light}%)`;
}

interface ListItemProps {
  list: { 
    id: string; 
    name: string; 
    created_at: string; 
  };
  activeListId: string | null;
  onSelect: (listId: string) => void;
  onEdit: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  // Remove taskCount and priorityCounts props
  // taskCount: number;
  // priorityCounts: {...};
  index: number;
  totalLists: number;
}

// Remove prioritizedCategories unnecessary now

const ListItem: React.FC<ListItemProps> = ({
  list,
  activeListId,
  onSelect,
  onEdit,
  onDelete,
  // taskCount,
  // priorityCounts,
  index,
  totalLists,
}) => {
  const [editingName, setEditingName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isActive = list.id === activeListId;
  const edgeColor = getListEdgeColor(index, totalLists);

  // --- Handlers ---
  const handleEditClick = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation();
    setEditingName(list.name);
    setIsEditing(true);
  };

  const handleCancelEdit = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation();
    setIsEditing(false);
    setEditingName('');
  };

  const handleSaveEdit = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation();
    if (editingName.trim() && editingName !== list.name) {
      onEdit(list.id, editingName.trim());
    }
    setIsEditing(false);
    setEditingName('');
  };

  const handleDeleteClick = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteDialog(false);
    onDelete(list.id);
  };

  // --- Card with colored edge only ---
  return (
    <>
      {/* Outer rounded with color edge */}
      <div
        className={cn(
          "relative w-full mx-auto cursor-pointer overflow-visible",
          "rounded-xl max-w-full"
        )}
        style={{
          background: edgeColor,
          borderRadius: '0.75rem',
          transition: 'box-shadow 0.2s, border 0.2s',
        }}
        onClick={() => {
          if (!isEditing) {
            onSelect(list.id);
          }
        }}
      >
        {/* Card content fills except left 10px for gradient */}
        <div
          className={cn(
            getCardBaseClasses(false),
            "pl-5 pr-5 py-3.5 sm:pl-5 sm:pr-5 sm:py-3.5",
            "bg-white dark:bg-[#191c23]",
            "relative w-full h-full",
            "rounded-xl",
          )}
          style={{
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderTopRightRadius: '0.75rem',
            borderBottomRightRadius: '0.75rem',
            position: 'relative',
            left: 10,
            width: "calc(100% - 10px)", // take full width except colored edge
            minHeight: 56,
          }}
          onClick={(e) => isEditing ? e.stopPropagation() : undefined}
        >
          {/* Active List Indicator: Pulsing dot at bottom-right for selected card */}
          {isActive && (
            <TaskActiveDot className="absolute bottom-2 right-2" />
          )}

          <div className={getCardHeaderClasses()}>
            {isEditing ? (
              <div className="flex items-center gap-2 flex-1">
                <Input 
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  className="flex-1 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-blue-500"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveEdit(e as any);
                    } else if (e.key === 'Escape') {
                      handleCancelEdit(e as any);
                    }
                  }}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSaveEdit}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 h-8 w-8"
                >
                  <Save className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCancelEdit}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <h3 className={getCardTitleClasses()}>{list.name}</h3>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className={getCardActionButtonClasses()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical size={16} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end"
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg z-[9999]"
                  >
                    <DropdownMenuItem onClick={handleEditClick}>
                      <Pencil size={14} className="mr-2" />
                      Edit title
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDeleteClick}>
                      <Trash2 size={14} className="mr-2" />
                      Delete list
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
          
          <p className={getCardDateClasses()}>
            Created on {format(new Date(list.created_at), 'MMM d, yyyy')}
          </p>
          
          {/* Remove metadata and priority/number icons */}
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-gray-800 border-gray-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete List</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              This will permanently delete this list and all associated tasks. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete list
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ListItem;

// --- Note: this file is getting quite long (over 280 lines). 
// Please consider splitting it up after reviewing this change for easier maintenance!
